import PropTypes from 'prop-types';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import * as MessageTypes from '../constants/messageTypes';
import { MESSAGE_TYPES_WITH_STANDARD_PLATFORM_DATA_STORAGE_FORMAT } from '../constants/integrations';
import { handleError } from 'services/ErrorService/ErrorService';
import { SIMPLR_BOT } from 'constants/sources';

// must .include customerPtr, lastMessagePtr (if exists), lastMessagePtr.couponCodePtr (if exists)
export const processNominatedOrReviewTicket = (ticket, nominationExpiresAt) => {
  if (!ticket.customerPtr) {
    handleError({ customMessage: 'customerPtr must be included', customFields: ticket });
    throw new Error('customerPtr must be included');
  }

  const [platformTicketId, platformDomain] = extractPlatformData(ticket);
  const nominationExpiresAtDate =
    nominationExpiresAt && nominationExpiresAt instanceof Date && nominationExpiresAt.toISOString();
  const combinedTags = typeof ticket.combinedTags === 'string' ? JSON.parse(ticket.combinedTags) : ticket.combinedTags;
  const shouldDisplayRecommendedMLTags =
    combinedTags &&
    (typeof combinedTags.shouldDisplayRecommendedMLTags === 'undefined' || combinedTags.shouldDisplayRecommendedMLTags);

  return {
    ticketId: ticket.objectId,
    conversationSid: ticket.conversationSid,
    ticketType: ticket.conversationType,
    fromChannel: ticket.customerChannel,
    subject: ticket.subject,
    ticketBody: ticket.lastMessageBody,
    smbName: ticket.smbName,
    unreadCount: ticket.unreadCount,
    createdAt: ticket.createdAt,
    updatedAt: ticket.updatedAt,
    conversationUpdatedAt: ticket.conversationUpdatedAt,
    simplrChannel: ticket.simplrChannel,
    associatedOrderNumber: ticket.associatedOrderNumber,
    amazonOrderNumber: ticket.amazonOrderNumber, // can't find this field in db
    simplrAccountPtrId: ticket.simplrAccountPtr?.objectId || ticket.simplrAccountId,
    simplrTicketNumber: ticket.simplrTicketNumber,
    escalateToSmbEmail: ticket.simplrAccountPtr?.escalateEmail,
    escalatedForReview: ticket.escalatedForReview,
    ticketStatus: ticket.status,
    sentiment: combinedTags?.correctedSentiment,
    classifications: combinedTags?.correctedClassifications,
    categories: combinedTags?.correctedCategories,
    simplrCategories: combinedTags?.simplrCategories,
    tagHistory: ticket.tagHistory || [],
    expertFilter: combinedTags?.correctedExpertFilter,
    recommendedMLTags: combinedTags?.recommendedMLTags || [],
    shouldDisplayRecommendedMLTags,
    recommendedMLTagsRequestDetails: combinedTags?.recommendedMLTagsRequestDetails,
    multiIntentClassification: combinedTags?.multiIntentClassification || MessageTypes.SINGLE_INTENT,
    customerPtrId: ticket.customerPtr?.objectId,
    customerEmail: ticket.customerPtr?.customerEmail,
    customerFirstName: ticket.customerPtr.customerFirstName,
    communicationType: ticket.communicationType,
    extraTicketInfo: ticket.extraTicketInfo,
    moreTicketInfo: ticket.moreTicketInfo,
    isTestTicket: ticket.isTestTicket,
    platformTicketId: platformTicketId,
    platformDomain: platformDomain,
    platformTags: ticket.platformTags,
    minimumExpertRank: ticket.simplrAccountPtr?.minimumExpertRank,
    smbAccountStatus: ticket.simplrAccountPtr?.accountStatus,
    lastMessageCouponCode: ticket.lastMessagePtr?.couponCodePtr?.code,
    lastMessageDirection: ticket.lastMessageDirection,
    lastMessageId: ticket.lastMessagePtr?.objectId ?? ticket.lastMessageId,
    unviewedMessageIds: [],
    nominationExpiresAt: nominationExpiresAtDate,
    brand: ticket.brand,
    mlData: ticket.mlData,
    escalationRecommendation: combinedTags?.escalationRecoData || {},
    platformAssigneeName: ticket.platformAssigneeName,
    lastMessageSource: ticket.lastMessagePtr?.source,
    qualityRating: ticket.qualityRating || {},
    reviewExpertId: ticket.reviewExpertPtr?.objectId ?? ticket.reviewExpertId,
    chatResponseState: ticket.chatResponseState,
    language: ticket.language,
  };
};

// must .include customerPtr, lastMessagePtr (if exists), lastMessagePtr.couponCodePtr (if exists)
export const processAssignedTicket = (ticket, nominatedExpertId) => {
  if (!ticket.customerPtr) {
    throw new Error('customerPtr must be included');
  }

  const expertId = ticket?.simplrExpertPtr?.objectId || ticket?.simplrExpertId || nominatedExpertId;

  const [platformTicketId, platformDomain] = extractPlatformData(ticket);
  const combinedTags = ticket.combinedTags;
  const shouldDisplayRecommendedMLTags =
    combinedTags &&
    (typeof combinedTags.shouldDisplayRecommendedMLTags === 'undefined' || combinedTags.shouldDisplayRecommendedMLTags);

  return {
    ticketId: ticket.objectId,
    conversationSid: ticket.conversationSid,
    ticketType: ticket.conversationType,
    fromChannel: ticket.customerChannel,
    subject: ticket.subject,
    ticketBody: ticket.lastMessageBody,
    smbName: ticket.smbName,
    unreadCount: ticket.unreadCount,
    createdAt: ticket.createdAt,
    updatedAt: ticket.updatedAt,
    conversationUpdatedAt: ticket.conversationUpdatedAt,
    simplrChannel: ticket.simplrChannel,
    associatedOrderNumber: ticket.associatedOrderNumber,
    amazonOrderNumber: ticket.amazonOrderNumber, // can't find this field in db
    simplrAccountPtrId: ticket.simplrAccountPtr?.objectId || ticket.simplrAccountId,
    simplrTicketNumber: ticket.simplrTicketNumber,
    escalateToSmbEmail: ticket.simplrAccountPtr?.escalateEmail,
    escalatedForReview: ticket.escalatedForReview,
    ticketStatus: ticket.status,
    sentiment: combinedTags?.correctedSentiment,
    classifications: combinedTags?.correctedClassifications,
    categories: combinedTags?.correctedCategories,
    simplrCategories: combinedTags?.simplrCategories,
    tagHistory: ticket.tagHistory || [],
    expertFilter: combinedTags?.correctedExpertFilter,
    recommendedMLTags: combinedTags?.recommendedMLTags || [],
    shouldDisplayRecommendedMLTags,
    recommendedMLTagsRequestDetails: combinedTags?.recommendedMLTagsRequestDetails,
    multiIntentClassification: combinedTags?.multiIntentClassification || MessageTypes.SINGLE_INTENT,
    expertId,
    customerPtrId: ticket.customerPtr.objectId ?? ticket.customerId,
    customerEmail: ticket.customerPtr.customerEmail,
    customerFirstName: ticket.customerPtr.customerFirstName,
    customerLastName: ticket.customerPtr.customerLastName,
    communicationType: ticket.communicationType,
    extraTicketInfo: ticket.extraTicketInfo,
    moreTicketInfo: ticket.moreTicketInfo,
    isTestTicket: ticket.isTestTicket,
    platformTicketId: platformTicketId,
    platformDomain: platformDomain,
    platformTags: ticket.platformTags,
    minimumExpertRank: ticket.simplrAccountPtr?.minimumExpertRank,
    smbAccountStatus: ticket.simplrAccountPtr?.accountStatus,
    lastMessageCouponCode: ticket.lastMessagePtr?.couponCodePtr?.code,
    lastMessageDirection: ticket.lastMessageDirection,
    lastMessageId: ticket.lastMessagePtr?.objectId ?? ticket.lastMessageId,
    unviewedMessageIds: [],
    brand: ticket.brand,
    mlData: ticket.mlData,
    escalationRecommendation: combinedTags?.escalationRecoData || {},
    platformAssigneeName: ticket.platformAssigneeName,
    lastMessageSource: ticket.lastMessagePtr?.source,
    qualityRating: ticket.qualityRating || {},
    chatResponseState: ticket.chatResponseState,
    pendingPartnerReachOut: ticket.pendingPartnerReachOut || {},
    language: ticket.language,
  };
};

export const processMessage = (message) => {
  if (message.direction === 'INBOUND' && !message.customerPtr) {
    throw new Error('customerPtr must be included');
  }

  let messageDateObj = new Date(message.createdAt);
  const originalCreatedAt = message.originalCreatedAt;
  let messageOriginalDateObj = originalCreatedAt ? new Date(originalCreatedAt) : messageDateObj;
  let messageType = message.messageType;

  let fromChannel = message.fromChannel;
  if (messageType && messageType.toLowerCase() === 'internal_note') {
    if (message.simplrExpertPtr && message.simplrExpertPtr.firstName) {
      fromChannel = `[${message.simplrExpertPtr.firstName} ${message.simplrExpertPtr.lastName}]`;
    } else {
      fromChannel = '[Internal Note]';
    }
  }

  const expert = message.simplrExpertPtr && {
    simplrExpertId: message.simplrExpertPtr.objectId,
    isAdmin: message.simplrExpertPtr.isAdmin,
    firstName: message.simplrExpertPtr.firstName,
    lastName: message.simplrExpertPtr.lastName,
    email: message.simplrExpertPtr.email,
    isSystemExpert: message.simplrExpertPtr.isSystemExpert,
    isSimplrBot: message.source === SIMPLR_BOT,
  };

  const messagePlain = message.messagePlain ? message.messagePlain.trim() : '';

  // only add signature on inbound messages
  const messageText =
    message.messageSignatureText && message.direction === 'INBOUND'
      ? message.messageBody + '\n' + message.messageSignatureText
      : message.messageBody;

  let processedMessage = {
    messageId: message.objectId,
    messageType: messageType,
    fromChannel: fromChannel,
    toChannel: message.toChannel,
    subject: message.subject,
    messagePlain,
    messageBody: messageText,
    messageBodyHtml: message.messageHtml,
    hiddenContent: message.hiddenContent,
    mediaUrl: message.mediaUrl,
    direction: message.direction,
    simplrAccountObjectId: message.simplrAccountPtr?.objectId ?? message.simplrAccountId,
    conversationObjectId: message.conversationPtr?.objectId ?? message.conversationId,
    createdAt: messageDateObj.toDateString() + '  ' + messageDateObj.toLocaleTimeString(),
    utcOriginalCreatedAt: messageOriginalDateObj ? messageOriginalDateObj.toISOString() : messageDateObj.toISOString(),
    smbName: message.smbName,
    source: message.source,
    hasRead: message.hasRead,
    // expert is undefined if message is inbound (made by customer)
    expert,
    status: message.status,
    customerFirstName: message.direction === 'INBOUND' ? message.customerPtr.customerFirstName : undefined,
    couponCode: message.couponCodePtr ? message.couponCodePtr.code : undefined,
    decisionTreeCouponCode: message.decisionTreeCouponCode,
    noteType: message.noteType,
    customerPtrId: message.customerPtr?.objectId ?? message.customerId,
    originalCreatedAt:
      messageOriginalDateObj &&
      messageOriginalDateObj.toDateString() + ' ' + messageOriginalDateObj.toLocaleTimeString(),
    detectedLanguage: message.detectedLanguage,
    translatedMessage: message.translatedMessage,
    originalMessage: message.originalMessage,
    flowDebugState: message?.flowDebugState,
  };

  if (messageType && MESSAGE_TYPES_WITH_STANDARD_PLATFORM_DATA_STORAGE_FORMAT.includes(messageType.toLowerCase())) {
    processedMessage.platformDomain = message.platformDomain;
    processedMessage.platformTicketId = message.platformTicketId;
  }

  if (messageType && messageType === 'INTERNAL_NOTE') {
    processedMessage.noteType = message.noteType;
  }

  return processedMessage;
};

//***************************************************************************************
// Important note: Columns in table simplrAccountPtr are not accessile to Expert users
//***************************************************************************************
export const processBusinessDetail = (businessDetail) => ({
  subBusinessVerticals: businessDetail.subBusinessVerticals,
  smbName: businessDetail.smbName,
  knowledgeBaseFolderUrl: businessDetail.knowledgeBaseFolderUrl,
  description: businessDetail.description,
  id: businessDetail.simplrAccountPtr?.objectId || businessDetail.simplrAccountId,
  homepageUrl: businessDetail.homepageUrl,
  businessLogoUrl: businessDetail.businessLogoUrl,
  historicalTicketSearch: businessDetail.historicalTicketSearch,
  simplrCategories: businessDetail.simplrCategories,
  responseGuideUrl: businessDetail.responseGuideUrl,
  extraBusinessInfo: businessDetail.extraBusinessInfo,
  expertRanks: businessDetail.simplrAccountPtr?.expertRanks,
  minimumExpertRank: businessDetail.simplrAccountPtr?.minimumExpertRank,
  useMenuOperator: businessDetail.simplrAccountPtr?.useMenuOperator,
  accountSid: businessDetail.simplrAccountPtr?.accountSid,
  accountStatus: businessDetail.simplrAccountPtr?.accountStatus,
  planFreeTrialDays: get(businessDetail, ['simplrAccountPtr', 'planPtr', 'freeTrialDays']),
  accountType: businessDetail.accountType,
  blacklistWords: businessDetail.blacklistWords,
  blacklistedEmailIds: businessDetail.blacklistedEmailIds,
  blacklistedSubjectText: businessDetail.blacklistedSubjectText,
  blacklistedBodyText: businessDetail.blacklistedBodyText,
  blacklistRegexes: businessDetail.blacklistRegexes,
  customMacros: businessDetail.customMacros,
  useDiscountApplications: !!businessDetail.useDiscountApplications,
  ticketsSearchConfig: businessDetail.ticketsSearchConfig || {},
  partnerInfo: businessDetail.partnerInfo,
  brandOptions: businessDetail.brandOptions,
  homepageUrls: businessDetail.homepageUrls,
  conversationalCommerce: businessDetail.conversationalCommerce,
  tagsConfig: businessDetail.tagsConfig || {},
  flowDebugStateFavoriteFieldsList: businessDetail.flowDebugStateFavoriteFieldsList,
});

export const businessDefaultProps = PropTypes.shape({
  id: PropTypes.string.isRequired,
  smbName: PropTypes.string.isRequired,
});

export const processSimplrExpert = (simplrExpert) => ({
  id: simplrExpert.objectId,
  userId: simplrExpert.userPtr?.objectId ?? simplrExpert.userId,
  firstName: simplrExpert.firstName,
  lastName: simplrExpert.lastName,
  email: simplrExpert.email,
  isActive: simplrExpert.isActive,
  isSpecialist: simplrExpert.isSpecialist,
  availability: simplrExpert.availability,
  numEmailNominated: simplrExpert.numEmailNominated,
  numChatNominated: simplrExpert.numChatNominated,
  numChatUnansweredAssigned: simplrExpert.numChatUnansweredAssigned,
  numNonChatUnansweredAssigned: simplrExpert.numNonChatUnansweredAssigned,
  rank: simplrExpert.rank,
  isAdmin: simplrExpert.isAdmin,
  expertType: simplrExpert.expertType,
  blacklistedBusinesses: simplrExpert.blacklistedBusinessIds || [],
  blacklistedBusinessesForChat: simplrExpert.blacklistedBusinessIdsForChat || [],
  chatDisabled: simplrExpert.chatDisabled,
  expertClass: simplrExpert.expertClass,
  numChatActiveAssigned: simplrExpert.numChatActiveAssigned,
  pastNominatedEmails: simplrExpert.pastNominatedEmails,
  pastNominatedChats: simplrExpert.pastNominatedChats,
  lastActivityAt: simplrExpert.lastActivityAt,
  chatMode: simplrExpert.chatMode,
  partnerProtectorWhitelist: simplrExpert.partnerProtectorWhitelist,
});

export const processBusinessAlerts = (businessAlert) => ({
  id: businessAlert.objectId,
  createdAt: businessAlert.createdAt,
  updatedAt: businessAlert.updatedAt,
  title: businessAlert.title,
  content: businessAlert.content,
  startDate: businessAlert.startDate,
  expirationDate: businessAlert.expirationDate,
  show: false,
  scope: businessAlert.scope,
  type: businessAlert.type,
  note: businessAlert.note,
  status: businessAlert.status,
  createdBy: businessAlert.simplrExpertPtr?.firstName,
  blockType: businessAlert.blockType,
  brands: businessAlert.brands,
});

export const processCouponCodeType = (couponCodeType) => ({
  id: couponCodeType.objectId,
  name: couponCodeType.name,
  simplrAccountId: couponCodeType.simplrAccountPtr.objectId,
  isDecisionTreeCoupon: couponCodeType.isDecisionTreeCoupon,
});

export const processOperatorMenu = (operatorMenu) => ({
  id: operatorMenu.id,
  message: operatorMenu.get('message'),
});

export const processOperatorMenuOption = (operatorMenuOption) => ({
  id: operatorMenuOption.id,
  displayLabel: operatorMenuOption.get('displayLabel'),
  orderPosition: operatorMenuOption.get('orderPosition'),
  replyText: operatorMenuOption.get('replyText'),
  inScopeMessage: operatorMenuOption.get('inScopeMessage'),
  outOfScopeMessage: operatorMenuOption.get('outOfScopeMessage'),
  simplrTag: operatorMenuOption.get('simplrTag'),
  saved: true,
});

const extractPlatformData = (ticket) => {
  let platformTicketId = ticket.platformTicketId;
  let platformDomain = ticket.platformDomain;

  return [platformTicketId, platformDomain];
};

// creates store objects like nominatedTickets, assignedTickets, allTickets
export const createQueueObject = (processedTickets) => {
  return {
    allTickets: processedTickets.map((ticket) => ticket.ticketId),
    ...keyBy(processedTickets, (ticket) => ticket.ticketId),
  };
};
